/**
 * SSE 对话 Hook（doctorclaw/msg 新协议）
 * 负责处理 SSE 流式对话的所有逻辑
 * 消息数据写入 Zustand store，支持多会话
 *
 * 与旧协议的主要差异：
 *  - 请求格式：chatData + msg.payload（替代 session_id + query）
 *  - 响应格式：ImFlow 卡片（data.message[].data.content.data.content.list）
 *  - session 管理：sessionId 由外部提供（coredata），不从 SSE 响应中获取
 *  - 用户消息 ID：ctrlData.userMsgACK.userMsgID（替代 session.request_msg_id）
 *  - bot 消息 ID：message[0].meta.msgId（替代 session.response_msg_id）
 *  - 完成信号：action === 'end'（替代 stop_type）
 *  - 取消对话：直接关闭 SSE client，无需调用 cancelConversation 接口
 */
import { useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import wsuiApi from '@api/wsuiApi';
import type { IMessage, IToolTip, ISection } from '@typing/wsui/getMessages';
import type { IConversationResponse, ISSESection, IMsgPayloadItem } from '@typing/wsui/msg';
import { ConversationSceneType } from '@typing/wsui/msg';
import type { ImFlowContentItem, ImFlowItem } from '@typing/wsui/msg';
import { useChatStore } from '@store/chatStore';
import { useSSENoticeStore } from '@store/sseNoticeStore';
import { message as messageAntd } from 'antd';
import { SSEClient } from '@utils/http/sse';
import { showCaptcha, subscribeCaptchaResult } from '@utils/biocFacade';
import { getAcsToken } from '@utils/initParis';

interface UseSSEConversationOptions {
    sessionId?: string;
    onError: (error: string) => void;
}

/**
 * Segment 元信息类型
 */
interface SegmentMeta {
    section_idx: string;
    type:
        | 'thinkProcessV2'
        | 'oeReferences'
        | 'markdown'
        | 'followQuestion'
        | 'text'
        | 'appendAsk'
        | 'fileCard'
        | 'mediaList';
    is_finished: boolean;
    tool_tip?: IToolTip;
}

/** 生成随机 10 位消息 key */
function generateMsgKey(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

function parseFeatureToToolTip(feature?: ImFlowContentItem['feature'] & { attributeStatus?: number }): IToolTip {
    const tools: Array<{ type: 'like' | 'unlike' | 'copy'; status?: number; popupConfig?: unknown }> = [];

    if (feature?.copyInfo?.enable) {
        tools.push({ type: 'copy' });
    }
    if (feature?.like) {
        tools.push({
            type: 'like',
            status: (feature as any).attributeStatus === 1 ? 1 : 0,
            popupConfig: feature.like.actionInfo?.interactionInfo?.popupInfo?.content,
        });
    }
    if (feature?.dislike) {
        tools.push({
            type: 'unlike',
            status: (feature as any).attributeStatus === 2 ? 1 : 0,
            popupConfig: feature.dislike.actionInfo?.interactionInfo?.popupInfo?.content,
        });
    }

    return (tools.length > 0 ? { tools } : {}) as IToolTip;
}

function parseReferenceFeatureToToolTip(
    feature?: ImFlowContentItem['feature'],
    fallbackFragmentId?: string
): IToolTip & { fragment_id?: string } {
    const toolTip = parseFeatureToToolTip(feature) as IToolTip & { fragment_id?: string };
    const fragmentId =
        feature?.like?.actionInfo?.interactionInfo?.popupInfo?.content?.submitBtn?.interactionInfo?.params
            ?.fragmentId ||
        feature?.dislike?.actionInfo?.interactionInfo?.popupInfo?.content?.submitBtn?.interactionInfo?.params
            ?.fragmentId ||
        fallbackFragmentId;

    if (fragmentId) {
        toolTip.fragment_id = fragmentId;
    }

    return toolTip;
}

function normalizeReferencesData(contentReplace: unknown): unknown {
    if (!contentReplace || typeof contentReplace !== 'object') {
        return contentReplace;
    }

    const data = contentReplace as { references?: any[]; list?: any[] };
    const references = Array.isArray(data.references)
        ? data.references
        : Array.isArray(data.list)
          ? data.list
          : undefined;

    if (!references) {
        return contentReplace;
    }

    return {
        ...data,
        references: references.map((reference) => ({
            ...reference,
            tool_tip: reference.tool_tip || parseReferenceFeatureToToolTip(reference.feature, reference.id),
        })),
    };
}

function getSegmentType(component?: string): ISSESection['segments'][number]['type'] {
    if (component === 'thinkProcessV2') {
        return 'thinkProcessV2';
    }
    if (component === 'oeReferences') {
        return 'oeReferences';
    }
    if (component === 'fileCard') {
        return 'fileCard';
    }
    if (component === 'mediaList') {
        return 'mediaList';
    }
    return 'markdown';
}

/**
 * 将 ImFlow list 转换为 ISSESection[]，供 accumulateSegmentData 处理
 * 每个 sectionId → section，每个 contentList key → segment
 */
function parseImFlowToSSESections(flowList: ImFlowItem[]): ISSESection[] {
    return flowList.map((item) => {
        const sectionId = String(item.sectionId);
        const ids = item.contentList?.ids || [];
        const data = item.contentList?.data || {};

        const segments = ids.map((key) => {
            const contentItem = data[key];
            const type = getSegmentType(contentItem?.component);
            const contentReplace =
                type === 'thinkProcessV2' || type === 'oeReferences'
                    ? normalizeReferencesData(contentItem?.contentReplace)
                    : contentItem?.contentReplace;

            return {
                segment_idx: key,
                type,
                content:
                    type === 'thinkProcessV2' || type === 'oeReferences'
                        ? contentReplace !== undefined
                            ? JSON.stringify(contentReplace)
                            : ''
                        : type === 'fileCard'
                          ? contentReplace !== undefined
                              ? JSON.stringify(contentReplace)
                              : ''
                          : type === 'mediaList'
                            ? contentReplace !== undefined
                                ? JSON.stringify(contentReplace)
                                : ''
                        : contentItem?.contentAdd || '',
                is_finished: item.isFinish || Boolean(contentItem?.isFinish) || false,
                tool_tip: parseFeatureToToolTip(contentItem?.feature),
            };
        });

        return {
            section_idx: sectionId,
            segments,
            tool_tip: {} as IToolTip,
        };
    });
}

export const useSSEConversation = (options: UseSSEConversationOptions) => {
    const { sessionId, onError } = options;

    const navigate = useNavigate();
    const {
        notifySessionTitleUpdate,
        notifyNewSession,
        notifySSECompleted,
        getSession,
        getLastBotMessageId,
        addMessage,
        updateMessage,
        setPageTitle,
        initSession,
        clearSession,
        setSSELoading,
        markMessageAsUserCancelled,
        expandThinking,
    } = useChatStore();

    const { referencesReadyNotice } = useSSENoticeStore();

    // SSE 相关 refs
    const sseClientRef = useRef<SSEClient | null>(null);
    const sseContentRef = useRef<Record<string, string>>({});
    const segmentMetaRef = useRef<Record<string, SegmentMeta>>({});
    const sessionIdUpdatedRef = useRef(false);
    const titleUpdatedRef = useRef(false);
    const currentBotMessageIdRef = useRef<string>('');
    const currentUserMessageIdRef = useRef<string>('');
    const mockBotMessageIdRef = useRef<string>('');
    const activeSessionIdRef = useRef<string>(sessionId || '');
    const expandedThinkingKeysRef = useRef<Set<string>>(new Set());
    const loadingClearedRef = useRef(false);
    const isHoldRef = useRef(false);
    const captchaRetryingRef = useRef(false);
    const conversationParamsRef = useRef<{
        query: string;
        targetSessionId: string;
        tempSessionId?: string;
        userMessageId?: string;
        botMessageId?: string;
        sceneType?: ConversationSceneType;
    } | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const startConversationRef = useRef<any>(null);

    // ==================== 匀速输出缓冲区 ====================
    const charQueueRef = useRef<Record<string, string[]>>({});
    const intervalIdRef = useRef<number | null>(null);
    const TOKEN_INTERVAL_MS = 20;
    const lastConsumeTimeRef = useRef<number>(0);
    const userCancelledRef = useRef(false);
    const ATOM_TOKEN_RE = /<span\s+class="ref-index"[^>]*>[^<]*<\/span>|[a-zA-Z0-9_]+|\s+|./gs;

    const stopConsumeLoop = useCallback(() => {
        if (intervalIdRef.current !== null) {
            cancelAnimationFrame(intervalIdRef.current);
            intervalIdRef.current = null;
        }
    }, []);

    const resetSSEState = useCallback(
        (hasExistingSession: boolean, sessionId?: string, tempSessionId?: string) => {
            sseContentRef.current = {};
            segmentMetaRef.current = {};
            sessionIdUpdatedRef.current = hasExistingSession;
            titleUpdatedRef.current = false;
            currentBotMessageIdRef.current = '';
            currentUserMessageIdRef.current = '';
            mockBotMessageIdRef.current = '';
            activeSessionIdRef.current = sessionId || tempSessionId || '';
            expandedThinkingKeysRef.current = new Set();
            loadingClearedRef.current = false;
            isHoldRef.current = false;
            charQueueRef.current = {};
            stopConsumeLoop();
            lastConsumeTimeRef.current = 0;
            userCancelledRef.current = false;
        },
        [stopConsumeLoop]
    );

    // ==================== 职责1：Session ID 管理 ====================
    const handleNewSessionId = useCallback(
        (sessionId: string) => {
            navigate(`/workbench/web/chat/${sessionId}`, { replace: true });
            notifyNewSession(sessionId);

            const tempSessionId = activeSessionIdRef.current;
            if (tempSessionId && tempSessionId.startsWith('temp-')) {
                const tempSession = getSession(tempSessionId);
                if (tempSession && tempSession.messages.length > 0) {
                    initSession(sessionId, tempSessionId);
                    clearSession(tempSessionId);
                }
            } else {
                initSession(sessionId);
            }

            activeSessionIdRef.current = sessionId;
            sessionIdUpdatedRef.current = true;
        },
        [navigate, notifyNewSession, getSession, initSession, clearSession]
    );

    // ==================== 职责2：Session Title 更新 ====================
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSessionTitle = useCallback(
        (sessionId: string | undefined, sessionTitle: string) => {
            const targetSessionId = activeSessionIdRef.current;
            if (targetSessionId) {
                setPageTitle(targetSessionId, sessionTitle);
            }
            titleUpdatedRef.current = true;
            if (sessionId) {
                notifySessionTitleUpdate(sessionId, sessionTitle);
            }
        },
        [setPageTitle, notifySessionTitleUpdate]
    );

    // ==================== 职责3：消息 ID 更新 ====================
    const updateUserMessageId = useCallback(
        (requestMsgId: string) => {
            const targetSessionId = activeSessionIdRef.current;
            if (!currentUserMessageIdRef.current || !targetSessionId) return;
            if (currentUserMessageIdRef.current === requestMsgId) return;
            updateMessage(targetSessionId, currentUserMessageIdRef.current, (msg) => ({
                ...msg,
                message_id: requestMsgId,
            }));
            currentUserMessageIdRef.current = requestMsgId;
        },
        [updateMessage]
    );

    const markAllSegmentsFinished = useCallback(() => {
        Object.keys(segmentMetaRef.current).forEach((key) => {
            segmentMetaRef.current[key].is_finished = true;
        });
    }, []);

    // ==================== 职责4：消息内容累积 ====================
    const accumulateSegmentData = useCallback(
        (sections: ISSESection[]) => {
            sections.forEach((section) => {
                section.segments.forEach((segment) => {
                    const segmentKey = `${section.section_idx}_${segment.segment_idx}`;
                    const existingMeta = segmentMetaRef.current[segmentKey];
                    const existingContent = sseContentRef.current[segmentKey];

                    if (segment.type === 'markdown' && !segment.content && !existingMeta && !existingContent) {
                        return;
                    }

                    if (
                        segment.type === 'thinkProcessV2' ||
                        segment.type === 'oeReferences' ||
                        segment.type === 'fileCard' ||
                        segment.type === 'mediaList'
                    ) {
                        if (segment.content) {
                            sseContentRef.current[segmentKey] = segment.content;
                            if (segment.type === 'thinkProcessV2' || segment.type === 'oeReferences') {
                                try {
                                    const data = JSON.parse(segment.content);
                                    if (data?.references && data.references.length >= 1) {
                                        const targetSessionId = activeSessionIdRef.current;
                                        const msgId = currentBotMessageIdRef.current;
                                        if (targetSessionId && msgId) {
                                            referencesReadyNotice(targetSessionId, msgId, data.references.length);
                                        }
                                    }
                                } catch {
                                    // JSON 解析失败，忽略
                                }
                            }
                        }
                    } else if (segment.type === 'markdown' && segment.content) {
                        if (!charQueueRef.current[segmentKey]) {
                            charQueueRef.current[segmentKey] = [];
                        }
                        if (sseContentRef.current[segmentKey] === undefined) {
                            sseContentRef.current[segmentKey] = '';
                        }

                        const queue = charQueueRef.current[segmentKey];
                        ATOM_TOKEN_RE.lastIndex = 0;
                        let token: RegExpExecArray | null;
                        while ((token = ATOM_TOKEN_RE.exec(segment.content)) !== null) {
                            queue.push(token[0]);
                        }
                    } else if (segment.content) {
                        sseContentRef.current[segmentKey] = (sseContentRef.current[segmentKey] || '') + segment.content;
                    }

                    const hasNewToolTip = segment.tool_tip?.tools && segment.tool_tip.tools.length > 0;

                    segmentMetaRef.current[segmentKey] = {
                        section_idx: section.section_idx,
                        type: segment.type,
                        is_finished: segment.is_finished || existingMeta?.is_finished || false,
                        tool_tip: hasNewToolTip ? segment.tool_tip : existingMeta?.tool_tip,
                    };
                });
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [referencesReadyNotice]
    );

    // ==================== 职责5：Bot 消息构建 ====================
    const buildBotMessage = useCallback((): IMessage => {
        if (!currentBotMessageIdRef.current) {
            currentBotMessageIdRef.current = `bot-temp-${Date.now()}`;
        }

        const sectionMap = new Map<string, Array<{ segmentKey: string; segment_idx: string }>>();
        Object.keys(segmentMetaRef.current).forEach((segmentKey) => {
            const meta = segmentMetaRef.current[segmentKey];
            const segment_idx = segmentKey.split('_').slice(1).join('_');

            if (!sectionMap.has(meta.section_idx)) {
                sectionMap.set(meta.section_idx, []);
            }
            sectionMap.get(meta.section_idx)!.push({ segmentKey, segment_idx });
        });

        // 按 section_idx 排序，只展示前序 section 已完成的后续 section
        const sortedSectionKeys = Array.from(sectionMap.keys()).sort((a, b) => Number(a) - Number(b));

        const visibleSections: ISection[] = [];
        for (const section_idx of sortedSectionKeys) {
            // 检查前面所有 section 是否都已完成且字符队列为空
            if (visibleSections.length > 0) {
                const prevSectionIdx = sortedSectionKeys[visibleSections.length - 1];
                const prevSegments = sectionMap.get(prevSectionIdx) || [];
                const prevAllFinished = prevSegments.every((s) => {
                    const meta = segmentMetaRef.current[s.segmentKey];
                    const queueEmpty =
                        !charQueueRef.current[s.segmentKey] || charQueueRef.current[s.segmentKey].length === 0;
                    return meta.is_finished && queueEmpty;
                });
                if (!prevAllFinished) break;
            }

            const segments = sectionMap.get(section_idx)!;
            visibleSections.push({
                section_idx,
                segments: segments.map(({ segmentKey, segment_idx }) => {
                    const meta = segmentMetaRef.current[segmentKey];
                    return {
                        segment_idx,
                        type: meta.type,
                        content: sseContentRef.current[segmentKey] || '',
                        is_finished: meta.is_finished,
                        tool_tip: meta.tool_tip || {},
                    };
                }),
                tool_tip: {},
            } as ISection);
        }

        return {
            message_id: currentBotMessageIdRef.current,
            poster: { name: 'AI助手', avatar: '', role: 'bot' },
            stop_type: undefined,
            sections: visibleSections,
        };
    }, []);

    // ==================== 职责6：Store 写入 ====================
    const saveBotMessageToStore = useCallback(
        (botMessage: IMessage) => {
            const targetSessionId = activeSessionIdRef.current;
            if (!targetSessionId) return;

            const existingSession = getSession(targetSessionId);
            const existingMsg = existingSession?.messages.find((m) => m.message_id === currentBotMessageIdRef.current);

            if (existingMsg) {
                updateMessage(targetSessionId, currentBotMessageIdRef.current, () => botMessage);
            } else {
                addMessage(targetSessionId, botMessage);
            }
        },
        [getSession, updateMessage, addMessage]
    );

    const syncBotMessageToStore = useCallback(
        (flushFirst = false, isLoading?: boolean) => {
            if (userCancelledRef.current) return;
            if (flushFirst) {
                const hasPendingTokens = Object.values(charQueueRef.current).some((q) => q.length > 0);
                if (hasPendingTokens) {
                    for (const segmentKey of Object.keys(charQueueRef.current)) {
                        const q = charQueueRef.current[segmentKey];
                        if (q.length === 0) continue;
                        sseContentRef.current[segmentKey] = (sseContentRef.current[segmentKey] || '') + q.join('');
                        q.length = 0;
                    }
                    stopConsumeLoop();
                }
            }
            const botMessage = {
                ...buildBotMessage(),
                isLoading: isLoading ?? true,
                isHold: isHoldRef.current,
            };
            saveBotMessageToStore(botMessage);
        },
        [buildBotMessage, saveBotMessageToStore, stopConsumeLoop]
    );

    // ==================== 职责7：匀速消费字符队列 ====================
    const consumeChars = useCallback(() => {
        let consumed = false;
        const queue = charQueueRef.current;

        for (const segmentKey of Object.keys(queue)) {
            const q = queue[segmentKey];
            if (q.length === 0) continue;
            const token = q.shift()!;
            sseContentRef.current[segmentKey] = (sseContentRef.current[segmentKey] || '') + token;
            consumed = true;
        }

        return consumed;
    }, []);

    const consumeLoop = useCallback(
        (timestamp: DOMHighResTimeStamp) => {
            intervalIdRef.current = null;

            if (timestamp - lastConsumeTimeRef.current >= TOKEN_INTERVAL_MS) {
                lastConsumeTimeRef.current = timestamp;
                const consumed = consumeChars();
                if (consumed) {
                    syncBotMessageToStore();
                }
            }

            const hasMore = Object.values(charQueueRef.current).some((q) => q.length > 0);
            if (hasMore) {
                intervalIdRef.current = requestAnimationFrame(consumeLoop);
            }
        },
        [consumeChars, syncBotMessageToStore]
    );

    const startConsumeLoop = useCallback(() => {
        if (intervalIdRef.current !== null) return;
        lastConsumeTimeRef.current = 0;
        intervalIdRef.current = requestAnimationFrame(consumeLoop);
    }, [consumeLoop]);

    const flushCharQueue = useCallback(() => {
        const queue = charQueueRef.current;
        let flushed = false;

        for (const segmentKey of Object.keys(queue)) {
            const q = queue[segmentKey];
            if (q.length === 0) continue;
            const remaining = q.join('');
            sseContentRef.current[segmentKey] = (sseContentRef.current[segmentKey] || '') + remaining;
            q.length = 0;
            flushed = true;
        }

        if (flushed) {
            syncBotMessageToStore();
        }

        stopConsumeLoop();
    }, [syncBotMessageToStore, stopConsumeLoop]);

    // ==================== 职责8：思考过程展开状态更新 ====================
    const updateThinkingExpandedState = useCallback(
        (sections: ISSESection[]) => {
            const targetSessionId = activeSessionIdRef.current;
            if (!targetSessionId) return;

            sections.forEach((section) => {
                section.segments.forEach((segment) => {
                    if (segment.type === 'thinkProcessV2') {
                        const segmentKey = `${currentBotMessageIdRef.current}_${segment.segment_idx}`;
                        if (!expandedThinkingKeysRef.current.has(segmentKey)) {
                            expandedThinkingKeysRef.current.add(segmentKey);
                            expandThinking(targetSessionId, segmentKey);
                        }
                    }
                });
            });
        },
        [expandThinking]
    );

    // ==================== 主处理函数 ====================
    const handleSSEMessage = useCallback(
        (response: IConversationResponse, isNewSession: boolean) => {
            // 1. 错误处理
            if (response.status === 232) {
                if (!captchaRetryingRef.current) {
                    captchaRetryingRef.current = true;
                    sseClientRef.current?.close(false);
                    showCaptcha();
                    subscribeCaptchaResult()
                        .then((stk: string) => {
                            captchaRetryingRef.current = false;
                            const params = conversationParamsRef.current;
                            if (params) {
                                startConversationRef.current?.(
                                    params.query,
                                    activeSessionIdRef.current || params.targetSessionId,
                                    params.tempSessionId,
                                    params.userMessageId,
                                    params.botMessageId,
                                    params.sceneType,
                                    { svcp_stk: stk }
                                );
                            }
                        })
                        .catch(() => {
                            captchaRetryingRef.current = false;
                            onError('验证失败，请重试');
                            const currentSessionId = activeSessionIdRef.current;
                            if (currentSessionId) setSSELoading(currentSessionId, false);
                        });
                }
                return;
            }

            if (response.status !== undefined && response.status !== 0) {
                console.error('SSE 响应错误:', response.msg);
                messageAntd.error(response.msg);
                throw new Error('sse 错误');
            }

            if (!response.data) return;

            const { message: msgList, ctrlData } = response.data;

            // 2.0 处理 convStatus hold（hold 帧可能不携带 message）
            if (ctrlData?.convStatus === 'hold' && !isHoldRef.current) {
                isHoldRef.current = true;
                syncBotMessageToStore(true, true);
                stopConsumeLoop();
            }

            // 2. 处理用户消息 ID（第一帧 ctrlData.userMsgACK.userMsgID）
            if (ctrlData?.userMsgACK?.userMsgID) {
                updateUserMessageId(ctrlData.userMsgACK.userMsgID);
            }

            // 3. 处理 bot 消息（ownerType === 2）
            const botMsg = msgList?.find((m) => m.meta.ownerType === 2);

            if (botMsg) {
                // 3.1 更新 bot 消息 ID
                if (botMsg.meta.msgId && botMsg.meta.msgId !== currentBotMessageIdRef.current) {
                    const oldBotMsgId = mockBotMessageIdRef.current || currentBotMessageIdRef.current;
                    const targetSessionId = activeSessionIdRef.current;

                    if (oldBotMsgId && oldBotMsgId !== botMsg.meta.msgId && targetSessionId) {
                        updateMessage(targetSessionId, oldBotMsgId, (msg) => ({
                            ...msg,
                            message_id: botMsg.meta.msgId!,
                        }));
                        mockBotMessageIdRef.current = '';
                    }

                    currentBotMessageIdRef.current = botMsg.meta.msgId;
                }

                // 3.2 处理 ImFlow 内容
                const flowList = botMsg.data.content?.data?.content?.list;
                if (flowList?.length) {
                    const sections = parseImFlowToSSESections(flowList);

                    accumulateSegmentData(sections);
                    if (ctrlData?.convStatus !== 'hold') {
                        isHoldRef.current = false;
                        syncBotMessageToStore(false, true);
                        startConsumeLoop();
                    }
                    updateThinkingExpandedState(sections);
                }

                // 3.4 firstToken 仅代表开始出 token，不关闭 session 级 loading
                if (ctrlData?.firstToken && !loadingClearedRef.current) {
                    loadingClearedRef.current = true;
                }

                // 3.5 action === 'end'：标记所有 segment 完成，flush，清除 loading
                if (botMsg.data.action === 'end') {
                    // 先把所有 segment 标记为完成，确保 flush 时不再显示光标
                    markAllSegmentsFinished();
                    isHoldRef.current = false;
                    syncBotMessageToStore(true, false);
                    const targetSessionId = activeSessionIdRef.current;
                    const msgId = currentBotMessageIdRef.current;

                    // 读取后端 isInterrupted 标识（与 vita 协议一致）
                    const contentData = botMsg.data.content?.data?.content;
                    const isInterrupted = contentData?.isInterrupted;

                    if (targetSessionId && msgId) {
                        updateMessage(targetSessionId, msgId, (msg) => ({
                            ...msg,
                            isLoading: false,
                            stop_type: isInterrupted ? 'user_cancel' : undefined,
                        }));
                    }
                    // 同时清除 session 级的 sseLoading
                    if (targetSessionId) {
                        setSSELoading(targetSessionId, false);
                    }
                }
            }

            // 4. 新会话时，使用 activeSessionIdRef 作为 session_id 导航
            // （session_id 来自 coredata，通过 targetSessionId 传入，不从 SSE 获取）
            if (isNewSession && !sessionIdUpdatedRef.current && activeSessionIdRef.current) {
                const realSessionId = activeSessionIdRef.current;
                if (!realSessionId.startsWith('temp-')) {
                    handleNewSessionId(realSessionId);
                }
            }
        },
        [
            onError,
            setSSELoading,
            updateUserMessageId,
            updateMessage,
            accumulateSegmentData,
            syncBotMessageToStore,
            startConsumeLoop,
            stopConsumeLoop,
            updateThinkingExpandedState,
            markAllSegmentsFinished,
            handleNewSessionId,
        ]
    );

    /**
     * 启动 SSE 对话
     */
    const startConversation = useCallback(
        async (
            query: string,
            targetSessionId: string = '',
            tempSessionId?: string,
            userMessageId?: string,
            botMessageId?: string,
            sceneType?: ConversationSceneType,
            extraHeaders?: Record<string, string>,
            images?: Array<{
                origin: string;
                icon: string;
                small: string;
                value?: string;
                path?: string;
                fileName?: string;
            }>
        ) => {
            const fkParams = await wsuiApi.fetchFkParams('ask');
            const acsToken = await getAcsToken();

            const realTargetSessionId = targetSessionId?.startsWith('temp-') ? '' : targetSessionId || '';

            if (!extraHeaders) {
                conversationParamsRef.current = {
                    query,
                    targetSessionId: realTargetSessionId,
                    tempSessionId,
                    userMessageId,
                    botMessageId,
                    sceneType,
                };
            }

            const isNewSession = !realTargetSessionId;
            const effectiveSessionId = realTargetSessionId || tempSessionId || '';

            resetSSEState(!isNewSession, realTargetSessionId, tempSessionId);

            // 在开始 SSE 前就设置 loading，而不是在每帧 onMessage 中设置
            if (effectiveSessionId) {
                setSSELoading(effectiveSessionId, true);
            }

            if (userMessageId) {
                currentUserMessageIdRef.current = userMessageId;
            }

            if (botMessageId) {
                mockBotMessageIdRef.current = botMessageId;
                currentBotMessageIdRef.current = botMessageId;
            }

            const msgKey = generateMsgKey();

            // 获取上一条消息 ID（用于 preMsgId）
            // const targetSessionId = realTargetSessionId || tempSessionId || "";
            const prevMsgId = targetSessionId
                ? getSession(targetSessionId)?.messages.slice(-1)[0]?.message_id
                : undefined;

            // 构建 payload：有图片时 content 为对象（contentType=7），纯文字时 content 为字符串（contentType=1）
            // 对齐 vita mix 页面 convertMsgToSSE 格式：payload 每项只包含 msgKey + content + contentType
            let payload: IMsgPayloadItem[];
            if (images && images.length > 0) {
                // richText 类型：contentType=7，value 传 BOS fileName（对齐 vita convertMsgToSSE 第243-262行）
                payload = [
                    {
                        msgKey,
                        content: {
                            text: query,
                            images: images.map((img) => ({
                                value: img.fileName || img.value || img.origin,
                                path: img.path || img.icon,
                            })),
                        },
                        contentType: 7,
                    },
                ];
            } else {
                // 纯文本类型：contentType=1（对齐 vita convertMsgToSSE 第265-271行）
                payload = [
                    {
                        msgKey,
                        content: query,
                        contentType: 1,
                    },
                ];
            }

            // 对齐 vita mix 页面 ctrlData：新会话首次请求传 firstCall=true（对齐 useConversationDataController 第844-860行）
            // 非 firstCall 时仅传 tmpBotVersion2
            const ctrlData = isNewSession
                ? { firstCall: true as const, tmpBotVersion2: 1 as const }
                : { tmpBotVersion2: 1 as const };

            // 对齐 vita mix 页面 multiTurn：非首次调用时计算当前 session 下用户提问轮次（含本次），首次=1（对齐第874-882行）
            const multiTurn = isNewSession
                ? undefined
                : (getSession(targetSessionId)?.messages.filter((m) => m.poster.role === 'user').length || 0) + 1;

            sseClientRef.current = wsuiApi.conversation(
                {
                    chatData: {
                        sessionId: realTargetSessionId,
                        // 对齐 vita mix 页面 sceneType 默认值 'unknown'（对齐 useConversationDataController 第836行）
                        sceneType: sceneType || 'unknown',
                    },
                    msg: {
                        ...(prevMsgId ? { preMsgId: prevMsgId } : {}),
                        payload,
                    },
                    ctrlData,
                    ...(multiTurn ? { multiTurn } : {}),
                    businessType: 'doctor_youyi',
                    appKey: 'pc_wenzhen',
                    ...fkParams,
                },
                {
                    onMessage: (data) => {
                        try {
                            const response: IConversationResponse = typeof data === 'string' ? JSON.parse(data) : data;
                            handleSSEMessage(response, isNewSession);
                        } catch (err) {
                            console.error('解析 SSE 数据失败:', err);
                        }
                    },
                    onError: (error) => {
                        console.error('SSE 错误:', error);
                        if (captchaRetryingRef.current) return;
                        flushCharQueue();
                        onError('获取响应失败，请稍后重试');
                        const currentSessionId = activeSessionIdRef.current;
                        if (currentSessionId) {
                            setSSELoading(currentSessionId, false);
                            // 标记用户消息发送失败
                            const userMsgId = currentUserMessageIdRef.current;
                            if (userMsgId) {
                                updateMessage(currentSessionId, userMsgId, (msg) => ({
                                    ...msg,
                                    sendFailed: true,
                                }));
                            }
                        }
                    },
                    onDone: () => {
                        if (userCancelledRef.current) return;
                        markAllSegmentsFinished();
                        isHoldRef.current = false;
                        syncBotMessageToStore(true, false);

                        const currentSessionId = activeSessionIdRef.current;
                        if (currentSessionId) {
                            setSSELoading(currentSessionId, false);
                            notifySSECompleted(currentSessionId);
                        }
                    },
                    headers: { 'Acs-Token': acsToken, ...extraHeaders },
                }
            );
        },
        [
            resetSSEState,
            getSession,
            setSSELoading,
            handleSSEMessage,
            flushCharQueue,
            onError,
            updateMessage,
            syncBotMessageToStore,
            markAllSegmentsFinished,
            notifySSECompleted,
        ]
    );

    /**
     * 关闭 SSE 连接（用户主动取消）
     * 直接关闭 SSE client，无需调用 cancelConversation 接口
     */
    const closeClient = useCallback(async () => {
        // 标记用户主动取消，防止 onDone 回调覆盖 stop_type
        userCancelledRef.current = true;

        // 先停止消费循环，防止后续 rAF 回调覆盖 stop_type
        stopConsumeLoop();

        let currentSessionId = activeSessionIdRef.current;
        let responseMsgId = currentBotMessageIdRef.current;

        if (!currentSessionId && sessionId) {
            currentSessionId = sessionId;
        }

        if (!responseMsgId && currentSessionId) {
            const lastBotMsgId = getLastBotMessageId(currentSessionId);
            if (lastBotMsgId) {
                responseMsgId = lastBotMsgId;
            }
        }

        // 关闭 SSE 连接
        sseClientRef.current?.close(true);

        // flush 剩余字符到 sseContentRef（不触发 syncBotMessageToStore）
        const queue = charQueueRef.current;
        for (const segmentKey of Object.keys(queue)) {
            const q = queue[segmentKey];
            if (q.length === 0) continue;
            sseContentRef.current[segmentKey] = (sseContentRef.current[segmentKey] || '') + q.join('');
            q.length = 0;
        }

        if (currentSessionId && responseMsgId) {
            // 构建最终消息，直接带上 stop_type，一次性写入 store
            const botMessage = {
                ...buildBotMessage(),
                isLoading: false,
                stop_type: 'user_cancel' as const,
            };
            updateMessage(currentSessionId, responseMsgId, () => botMessage);
        }

        if (currentSessionId) {
            setSSELoading(currentSessionId, false);
        }
    }, [sessionId, getLastBotMessageId, setSSELoading, updateMessage, buildBotMessage, stopConsumeLoop]);

    const sseLoading = useChatStore(
        useCallback(
            (state) => {
                return sessionId ? state.sessions[sessionId]?.sseLoading || false : false;
            },
            [sessionId]
        )
    );

    useEffect(() => {
        startConversationRef.current = startConversation;
    }, [startConversation]);

    return {
        startConversation,
        resetSSEState,
        getCurrentStreamingMessageId: () => currentBotMessageIdRef.current,
        closeClient,
        sseLoading,
    };
};
